import { ComponentFactoryResolver, ViewContainerRef, ComponentRef, ComponentFactory } from '@angular/core';
import CustomUIHandler from './customUIHandler';
import { HttpClient } from '@angular/common/http';
import { AuthenticatedUser } from '../otpSession+UI/authenticated-user.component';
import { TranslateService } from '@ngx-translate/core';
import { Brand, CDKCoreService } from '@rogers/cdk/core';
import get from 'lodash/get';
import { CookieService } from 'ngx-cookie-service';


declare var onSuccessResult: (username: string, authToken: string) => void;
declare var onErrorResult: (errorMessage: string) => void;


export interface AuthToken {
  platform: string;
  token: string;
  webAuthnClientId: string;
  drs_enable: boolean;
  brand: Brand;
  csmID: string;
  pid: string;
  aid: string;
  sid: string;
  prompt: string;
  hostname: string;
  authRequestContextToken: string;
  sessionContextToken: string;
  jsonResult: string;
  ui_locales: string;
  uuid: string;
  cross_session_message: any;
  IDMClientID: any;
}

export interface UIContext {
  viewContainerRef: ViewContainerRef;
  resolver: ComponentFactoryResolver;
}

export class TransmitService {
  clientContext: object;

  constructor(
    sdk: com.ts.mobile.sdk.TransmitSDKXm,
    uiHandler: com.ts.mobile.sdk.UIHandler,
    uiContext: UIContext,
    private http: HttpClient,
    private readonly translate: TranslateService,
    private coreService: CDKCoreService,
    private readonly cookieService: CookieService,
    authToken: AuthToken
  ) {
      this.applyAuthToken(authToken);
      this.uiContext = uiContext;
      this.setupTransmitSDK(sdk, uiHandler);
  }

  transmitSDK: com.ts.mobile.sdk.TransmitSDKXm;
  sdkInitialized: Promise<boolean> = Promise.resolve(false);
  uiContext: UIContext;
  useCustomUIHandler =  true;
  authenticatedUserRef: ComponentRef<AuthenticatedUser>;

  // Auth context returned by the profile-update authentication endpoint.
  pid: string = '';
  sid: string = '';
  aid: string = '';
  token: string = '';
  hostname: string = '';
  authRequestContextToken: string = '';
  sessionContextToken: string = '';
  ui_locales: string = '';
  platform: string = '';
  brand: Brand = '' as Brand;
  csmID: any;
  prompt: string = '';
  jsonResult: string = '';
  uuid: string = '';
  cross_session_message: any;
  showSPA: boolean = true;
  marketingCloudID: string;
  mcmid: string;
  xDomSessionGuid: string;

  onSuccessResult: (username: string, authToken: string) => void = onSuccessResult;
  onErrorResult: (errorMessage: string) => void = onErrorResult;

  private applyAuthToken(authToken: AuthToken): void {
    this.pid = authToken.pid;
    this.sid = authToken.sid;
    this.aid = authToken.aid;
    this.token = authToken.token;
    this.hostname = authToken.hostname;
    this.authRequestContextToken = authToken.authRequestContextToken;
    this.sessionContextToken = authToken.sessionContextToken;
    this.ui_locales = authToken.ui_locales;
    this.platform = authToken.platform;
    this.brand = authToken.brand;
    this.csmID = authToken.csmID;
    this.prompt = authToken.prompt;
    this.jsonResult = authToken.jsonResult;
    this.uuid = authToken.uuid;
    this.cross_session_message = authToken.cross_session_message;
  }

  private CONSTS = {
    SESSION_STORAGE_KEY: {
      TRANSMIT_TOKEN: 'transmit_token',
      CURRENT_USER_ID: 'transmit_current_user_id'
    }
  };

  public  authenticate(authToken: any,isDRSSDKloadFailed:boolean): Promise<com.ts.mobile.sdk.AuthenticationResult> {
    return new Promise<com.ts.mobile.sdk.AuthenticationResult>(async (resolve, reject) => {
      const self = this;
      // const loginJourneyName = 'validateGuidZoran'; // in case different brands have different journeys
      const policyId = self.pid; // validateGuidZoran
      let drsError;
      let drsSessionToken;

      try{
         drsSessionToken = await (window as any).tsPlatform.drs.getSessionToken();
         if(drsSessionToken.startsWith("disabled_sdk") || drsSessionToken.startsWith("communication_error")
            || drsSessionToken.startsWith("unexpected_error")){
          drsError = drsSessionToken;
          drsSessionToken = '';
         }
      }
      catch(error){
        drsError = error;
      }
    

      // (window as any).tsPlatform.drs.getSessionToken().then((result) => {
      //   if (typeof result === 'string'){
      //     drsSessionToken = result;
      //   }
      //   else{
      //      drsError = result?.actionToken;
      //   }
      // }).catch((error) => {
      //   drsError = error;
      //   console.error('DRS Token Error:' + error);
      // });

      const idpParams =
          {
            session_id: self.sid,
            token: authToken,
            '$idp_inline_completion': true,
            authRequestContext: self.authRequestContextToken !== '%authRequestContextToken%' ? self.authRequestContextToken : '',
            csmID: self.csmID,
            mktCloudID: self.mcmid,
            xDomSessionGuid: self.xDomSessionGuid,
            drs_sessionToken: drsSessionToken,
            drs_error: isDRSSDKloadFailed ? 'DRS_SDK_Failed' : drsError
          };

      this.clientContext = self.getClientContext();
      this.clientContext['cookieService'] = this.cookieService;

      self.transmitSDK.invokeAnonymousPolicy(
        policyId,
        idpParams,
        this.clientContext)
        .then((successfulAuth) => {
          const request = 'request';

          if (successfulAuth.getData() && successfulAuth.getData()[request] && successfulAuth.getData()[request].url)
          {
            this.showSPA = false;
            console.log('successful auth. redirect to ', successfulAuth.getData()[request].url);
            window.location = successfulAuth.getData()[request].url;
            // We need to keep this logout, because it is internal mechanism within transmit to clear 
            // native transmit session 
            self.transmitSDK.logout()
            .then(() => {
              // self.onSuccessResult(successfulAuth.getInternalData().json_data.username, successfulAuth.getToken());
              console.log('successfully logged out.');
            })
            .catch((logoutError) => {
              self.onErrorResult((logoutError.getMessage && logoutError.getMessage()) || logoutError.toString() );
            });

          }
          else{
            self.transmitSDK.logout();
          }
        })
        .catch((err) => {
          console.error(`Transmit service err : ${err}`);
          console.error(JSON.stringify(err,null,2));
          const error =  get(err.getData(), 'failure_data.reason.data.error');
          const error_code =  get(err.getData(), 'failure_data.reason.data.error_code');

          const errorDescription =  get(err.getData(), 'failure_data.reason.data.error_description');

          const reason_type = get(err.getData(), 'failure_data.reason.type');

          console.log('error : ', error);
          console.log('error_code : ', error_code);

          console.log('errorDescription : ', errorDescription);
          console.log('reason_type : ', reason_type);

          //console.log('error reason type', err.getData().failure_data.reason.type);
          //console.log('error error_code', err.getData().failure_data.reason.data.error_code);



          this.showSPA = false;
          const url = this.cookieService.get('redirect_uri');
          let appendChar = '?';
          if (url.includes('?'))
          {
             appendChar = '&';
          }

          if (errorDescription === 'invalid id_token_hint' || errorDescription === 'SSO not Found')
          {
            console.log('redirect_uri : ', url);
            const state = this.cookieService.get('state');
            console.log('state : ', state);
            let redirect_url = url + appendChar + 'error=' + error
            + '&error_description=' +  errorDescription
            + '&lang=' + this.cookieService.get('lang')
            + '&state=' + state;
            console.warn('redirect_url : ', redirect_url);
            window.location.href = redirect_url;
          }
          else if (error_code == 'UE10')
          {
            console.log('redirect_uri : ', url);
            const state = this.cookieService.get('state');
            console.log('state : ', state);
            let redirect_url = url + appendChar + 'error=' + error_code
            + '&error_description=' +  reason_type
            + '&lang=' + this.cookieService.get('lang')
            + '&state=' + state;
            console.warn('redirect_url : ', redirect_url);
            window.location.href = redirect_url;
          }         
          else if (reason_type == 'locked')
          {
            const regFlow = this.cookieService.get('regFlow');
            console.log('User Locked - regFlow: ', regFlow);

            const profilePage = this.cookieService.get('profilePage');

            let error_code = '';
            if (profilePage === 'recoveryNumber' || profilePage === 'changeUsername'){
              error_code = 'UE07';
            }
            else if (regFlow === 'SELF_REG' || regFlow === 'create'){
              error_code = 'UE08';
            }
            else {
              error_code = 'UE06';
            }

            const state = this.cookieService.get('state');
            let redirect_url = url + appendChar + 'error=' + error_code
                + '&error_description=' +  'Account Locked. Incorrect PIN limit exceeded'
                + '&lang=' + this.cookieService.get('lang')
                + '&state=' + state;
            console.warn('redirect_url : ', redirect_url);
            window.location.href = redirect_url;

          }
          else if (error == null)
          {
            const state = this.cookieService.get('state');
            let redirect_url = url + appendChar + 'error=' + 'SE01'
            + '&error_description=' +  'Sorry! Something went wrong. Please try again or check back later.'
            + '&lang=' + this.cookieService.get('lang')
            + '&state=' + state;
            console.warn('redirect_url : ', redirect_url);

            if (this.clientContext){
              const userId = this.clientContext['UserId'];
              if (userId !== null || userId !== undefined){
                this.transmitSDK.clearDataForUser(userId);
              }
            }
            window.location.href = redirect_url;

          }
          else
          {
            const state = this.cookieService.get('state');
            let redirect_url = url + appendChar + 'error=' + error
            + '&error_description=' +  errorDescription
            + '&lang=' + this.cookieService.get('lang')
            + '&state=' + state;
            console.warn('redirect_url : ', redirect_url);
            window.location.href = redirect_url;

          }
        });
    });
  }

  public authenticateWithUserId(userId: any,sessionId: string,authToken: string,csmID: string,isDRSSDKloadFailed: boolean): Promise<com.ts.mobile.sdk.AuthenticationResult> {
    return new Promise<com.ts.mobile.sdk.AuthenticationResult>(async (resolve, reject) => {
      const normalizedUserId = typeof userId === 'string' ? userId.trim() : userId;
      if (!normalizedUserId) {
        const errorMessage = 'Authenticate Error: Missing user id from preauthenticate response';
        console.error(errorMessage);
        reject({
          getMessage: () => errorMessage,
          toString: () => errorMessage
        });
        return;
      }

    let drsError;
      let drsSessionToken;

      try{
         drsSessionToken = await (window as any).tsPlatform.drs.getSessionToken();
         if(drsSessionToken.startsWith("disabled_sdk") || drsSessionToken.startsWith("communication_error")
            || drsSessionToken.startsWith("unexpected_error")){
          drsError = drsSessionToken;
          drsSessionToken = '';
         }
      }
      catch(error){
        drsError = error;
      }

      // this.userId = normalizedUserId;
      // this.sessionId = sessionId;
      // this.authToken = authToken;
      this.csmID = csmID;
      const policyId = 'CPM_user_journey';
      // const additionalParams = {
      //   "cross_session_message": crossSessionMessage 
      // };

       const additionalParams =
          {
            session_id: sessionId,
            token: authToken,
            '$idp_inline_completion': true,
            authRequestContext: this.authRequestContextToken !== '%authRequestContextToken%' ? this.authRequestContextToken : '',
            csmID: csmID,
            // mktCloudID: self.mcmid,
            // xDomSessionGuid: self.xDomSessionGuid,
            drs_sessionToken: drsSessionToken,
            drs_error: isDRSSDKloadFailed ? 'DRS_SDK_Failed' : drsError
          };

      const clientContext = this.getClientContext() as any;
      clientContext['cookieService'] = this.cookieService;
      this.clientContext = clientContext;

      this.transmitSDK.authenticate(String(normalizedUserId), policyId, additionalParams, this.clientContext)
        .then((successfulAuth: any) => {
          this.updateSessionToken(successfulAuth.getToken());
          this.updateSessionInfo(this.CONSTS.SESSION_STORAGE_KEY.CURRENT_USER_ID, String(normalizedUserId));
          this.transmitJourneyEnded();
          resolve(successfulAuth);
        })
        .catch((error: any) => {
          console.error(`Authenticate Error: ${error}`);
          this.clearUIHandlerContainer();
          reject(error);
        });
    });
  }

  /** Non UIFormSession interface Functions */
  private transmitJourneyEnded() {
    console.log(`Inside transmitJourneyEnded`);
    this.uiContext.viewContainerRef.clear();
    const factory: ComponentFactory<AuthenticatedUser> = this.uiContext.resolver.resolveComponentFactory(AuthenticatedUser);
    this.authenticatedUserRef = this.uiContext.viewContainerRef.createComponent(factory);
    this.authenticatedUserRef.instance.onLogout = this.logout;
  }


  public logout(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
        this.transmitSDK.logout()
            .then((success: boolean) => {
                this.updateSessionToken(null);
                this.updateSessionInfo(this.CONSTS.SESSION_STORAGE_KEY.CURRENT_USER_ID, null);
                resolve(true);
            });
    });
  }

  public getCurrentUserId(): string | null {
    return this.getSessionInfo(this.CONSTS.SESSION_STORAGE_KEY.CURRENT_USER_ID);
  }

  public isLoggedIn(): boolean {
    return this.getSessionToken() !== null;
  }

  public invokeJourney(policy: string, additionalParams: object): Promise<com.ts.mobile.sdk.AuthenticationResult> {
    return new Promise<com.ts.mobile.sdk.AuthenticationResult>((resolve, reject) => {
      this.transmitSDK.invokePolicy(policy, additionalParams, this.clientContext)
        .then((results: com.ts.mobile.sdk.AuthenticationResult) => {
            resolve(results);
        })
        .catch((error: com.ts.mobile.sdk.AuthenticationError) => {
            reject(error);
        });
    });
  }

  public clearUIHandlerContainer = (): void => {
    this.uiContext.viewContainerRef.clear();
  }

  private getClientContext = (): object | null => {
    return {
      uiContainer: this.uiContext.viewContainerRef.element.nativeElement, // *** used by the default ui handler ***
      viewContainerRef: this.uiContext.viewContainerRef,
      resolver: this.uiContext.resolver
      // uiContainer: transmitContainer.element.nativeElement
    };
  }

  private extractMCMID(cookieValue) {

    try {
        const arr = cookieValue.split("|MCMID|");
        let text = arr[1];    
        text = text.split('|')[0];
        return text;
      } 
    catch (error) 
      {
        console.error('error extracting MCMID');
        console.error(error.name);
        console.error(error.message);
        console.error(error.stack);
        return '';
      }
    
  }

  private getAnalyticsCookie(cookieName) {
    let cookie = '';
    try {      
      cookie =
        this.cookieService.get(decodeURIComponent(cookieName));
        console.log(cookieName, ' cookie value =>', cookie);

    } catch (error) {
      console.error('Cookie not found => ', cookieName);
    }

    return cookie;
  }

  private setupTransmitSDK(sdk: com.ts.mobile.sdk.TransmitSDKXm, uiHandler: com.ts.mobile.sdk.UIHandler) {
    const defaultUIHandler = new (uiHandler as any).XmUIHandler(); // cast to 'any' to use XmSdk
    this.transmitSDK = (sdk as any).XmSdk(); // cast to 'any' to use XmSdk

    // save the brand value in client before overriding
    this.cookieService.set('igniteBrand', this.brand, undefined, undefined, null, true, 'None');
    // this.cookieService.set('ignitetv_shaw', this.brand, undefined, undefined, null, true, 'None');

    let client = this.brand === 'cbu' ? 'rogers' : this.brand;
    client = client === 'cpp' ? 'chatr' : client;
    client = client === 'rshm' ? 'rogers' : client;
    client = client === 'xfi' ? 'rogers' : client;
    client = client === 'eastwebrem' ? 'ignitetv' : client;

    this.cookieService.set('client', client, undefined, undefined, null, true, 'None');

    this.brand = (this.brand === 'cbu') ? 'rogers' : this.brand;
    this.brand = (this.brand === 'cpp') ? 'chatr' : this.brand;

    this.brand = (this.brand === 'media' || this.brand === 'media_dis') ? 'fido' : this.brand;

    this.brand = (this.brand === 'yahoo') ? 'rogers' : this.brand;

    this.brand = (this.brand === 'ignitetv') ? 'rogers' : this.brand;

    this.brand = (this.brand === 'shaw' || this.brand === 'shaw_enterprise'
      || this.brand === 'shaw_direct') ? 'rogers' : this.brand;


    console.log('initial brand value - ' + this.brand);

    const igniteBrand = this.cookieService.get('igniteBrand');

    if (igniteBrand === 'rshm' || igniteBrand === 'xfi' || igniteBrand === 'ignitetv'
          || igniteBrand === 'homeconnect_shaw' || igniteBrand === 'eastwebrem'
          || igniteBrand === 'xfinity'
    ){
      this.brand = 'rogers';
    }

    this.cookieService.set('lang', this.ui_locales, undefined, undefined, null, true, 'None');
    this.cookieService.set('brand', this.brand, undefined, undefined, null, true, 'None');

    this.cookieService.set('csmID', this.csmID, undefined, undefined, null, true, 'None');
    this.cookieService.set('platform', this.platform, undefined, undefined, null, true, 'None');

    if (igniteBrand === 'homeconnect_shaw'){
      this.cookieService.set('ignite-tv-selection', 'shaw', undefined, undefined, null, true, 'None');
    }

    this.marketingCloudID = this.getAnalyticsCookie('AMCV_D7FD34FA53D63B860A490D44%40AdobeOrg');
    if (this.marketingCloudID)
    {
      this.mcmid = this.extractMCMID(this.marketingCloudID);
    }
    else
    {
      this.mcmid = '';
    }

    this.xDomSessionGuid = this.getAnalyticsCookie('X_DOM_session_guid');
    if (this.prompt === 'none')
    {
      this.showSPA = false;
    }
    else
    {
      this.showSPA = true;
    }
    this.translate.use(this.cookieService.get('lang'));
    this.brand = this.cookieService.get('brand');
console.log('brand value - ' + this.cookieService.get('brand'));

    // media and yahoo both use rogers cdk, so override brand valua
    if (this.brand === 'yahoo')
    {
      this.brand = 'rogers';
    }


    this.coreService.set({ brand: this.brand });

    if (this.useCustomUIHandler) {
      this.transmitSDK.setUiHandler(new CustomUIHandler());
    } else {
      this.transmitSDK.setUiHandler(defaultUIHandler);
    }

    this.transmitSDK.setConnectionSettings(this.getConnectionSettings());

    // Uncomment for debugging
    // -----------------------
    // this.transmitSDK.setLogLevel(com.ts.mobile.sdk.LogLevel.Debug);

    // Device fingerprinting configuration

    this.transmitSDK.setEnabledCollectorsForAnonymousJourneys(
      [
          com.ts.mobile.sdk.CollectorType.Capabilities,
          com.ts.mobile.sdk.CollectorType.DeviceDetails,
          com.ts.mobile.sdk.CollectorType.DeviceFingerprint,
          com.ts.mobile.sdk.CollectorType.ExternalSDKDetails
      ]);

    // this.transmitSDK.setLogLevel(com.ts.mobile.sdk.LogLevel.Debug);

    this.sdkInitialized = this.transmitSDK.initialize()
      .then((success: boolean) => {
        console.log('SDK Initialized successfully');

        setTimeout(() => this.checkUserLocked(), 1000);
        return success;

      }).catch((error: any) => {
        console.error(`Initialization error occurred: ${error.getMessage()}`);
        throw error;
      });
  }

    //  check if we have locked user dialog box in dom 
    checkUserLocked() {
      this.showSPA = false;
      let transmitContainerHTML = document.getElementById("transmitContainer").innerHTML;
      let str = 'User Locked';
      let lockedFound = transmitContainerHTML.includes(str);

      if (transmitContainerHTML.indexOf(str) !== -1){ 
        console.error('User is locked by Transmit');
        let continueBtn: HTMLElement= document.querySelectorAll("button.xmui-button")[0] as HTMLElement;
        console.error('continueBtn ', continueBtn);
        continueBtn.click();
      }
      else {
        console.error('User is NOT locked by Transmit');
        this.showSPA = true;

      }
  }

  private getConnectionSettings(): com.ts.mobile.sdk.SDKConnectionSettings {
    // based on aid being set
   // const serverUrl = this.aid !== '%aid%' ? '' : 'https://rogers-dev.transmit.security';
    const serverUrl = this.hostname;
    const appId = this.aid;
    const apiTokenId = null;
    const apiToken = null;
    const realm = '';
    const settings = com.ts.mobile.sdk.SDKConnectionSettings.create(serverUrl, appId, apiTokenId, apiToken);

    this.cookieService.set('oidc_sid', this.sid, undefined, undefined, null, true, 'None');
    if (realm) {
      settings.setRealm(realm);
    }

    return settings;
  }

  /** Session Storage */

  private updateSessionToken(tokenString: string | null) {
    if (!tokenString) {
      sessionStorage.removeItem(this.CONSTS.SESSION_STORAGE_KEY.TRANSMIT_TOKEN);
      return;
    }
    sessionStorage.setItem(this.CONSTS.SESSION_STORAGE_KEY.TRANSMIT_TOKEN, tokenString);
  }

  private getSessionToken() {
    return sessionStorage.getItem(this.CONSTS.SESSION_STORAGE_KEY.TRANSMIT_TOKEN);
  }

  private updateSessionInfo(key: string, value: string | null) {
    sessionStorage.setItem(key, value);
  }

  private getSessionInfo(key: string): string | null {
    return sessionStorage.getItem(key);
  }
}
